import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { Play, Square, X, Flame, Trophy, RefreshCw, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Same milestones as backend (display purposes)
const MILESTONE_HOURS = [4, 8, 12, 16, 24, 36];

function fmtHMS(seconds) {
  if (seconds < 0) seconds = 0;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function fmtClock(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('sv-SE', { weekday: 'short', hour: '2-digit', minute: '2-digit' });
}

function PresetModal({ presets, onPick, onClose }) {
  const [customHours, setCustomHours] = useState(16);
  return (
    <div className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose} data-testid="fasting-preset-modal">
      <div className="neo-card bg-white dark:bg-[#252545] max-w-md w-full p-5 max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xl font-bold font-['Fredoka']">Välj fastemål</h3>
          <button onClick={onClose} className="neo-btn p-1.5 bg-white" data-testid="fasting-preset-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {presets.map((p) => (
            <button key={p.id}
              onClick={() => p.id === 'custom' ? null : onPick(p.id)}
              disabled={p.id === 'custom'}
              className="neo-btn p-3 text-left bg-gradient-to-br from-[#FFE4E9] to-[#FFF5D9] hover:scale-[1.02] disabled:opacity-50 disabled:hover:scale-100"
              data-testid={`fasting-preset-${p.id}`}>
              <p className="font-bold font-['Fredoka'] text-base">{p.label}</p>
              <p className="text-[10px] opacity-70">{p.sub}{p.hours ? ` · ${p.hours}h` : ''}</p>
            </button>
          ))}
        </div>
        <div className="mt-4 p-3 rounded-2xl border-2 border-black bg-[#F5F5F5]">
          <p className="text-xs font-bold mb-2">Egen tid</p>
          <div className="flex items-center gap-2">
            <input type="number" min={1} max={168} value={customHours}
              onChange={(e) => setCustomHours(Math.max(1, Math.min(168, Number(e.target.value) || 1)))}
              className="flex-1 px-3 py-2 rounded-xl border-2 border-black bg-white text-sm"
              data-testid="fasting-custom-hours" />
            <span className="text-xs font-bold">timmar</span>
            <button onClick={() => onPick('custom', customHours)}
              className="neo-btn px-4 py-2 bg-[#B9FBC0] text-xs font-bold"
              data-testid="fasting-custom-start">
              Starta
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function EndModal({ elapsed, target, onSubmit, onClose }) {
  const [notes, setNotes] = useState('');
  const [mood, setMood] = useState('okay');
  const completed = elapsed >= target;
  return (
    <div className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose} data-testid="fasting-end-modal">
      <div className="neo-card bg-white dark:bg-[#252545] max-w-md w-full p-5"
        onClick={(e) => e.stopPropagation()}>
        <div className="text-center mb-3">
          <div className="text-5xl mb-1">{completed ? '🏆' : '💗'}</div>
          <h3 className="text-2xl font-bold font-['Fredoka']">
            {completed ? 'Du klarade det!' : 'Bra att du lyssnade på kroppen'}
          </h3>
          <p className="text-xs opacity-70 mt-1">
            {elapsed.toFixed(1)}h av {target}h ({Math.round((elapsed / target) * 100)}%)
          </p>
        </div>
        <p className="text-xs font-bold mb-2">Hur kändes det?</p>
        <div className="flex gap-2 mb-3">
          {[
            { id: 'easy', label: '😌 Lätt' },
            { id: 'okay', label: '🙂 Okej' },
            { id: 'hard', label: '😮‍💨 Tufft' },
          ].map((m) => (
            <button key={m.id} onClick={() => setMood(m.id)}
              className={`flex-1 px-3 py-2 rounded-xl border-2 border-black text-xs font-bold transition ${
                mood === m.id ? 'bg-[#FFC8DD] scale-105' : 'bg-white'
              }`}
              data-testid={`fasting-mood-${m.id}`}>
              {m.label}
            </button>
          ))}
        </div>
        <label className="block text-xs font-bold mb-3">
          Anteckning (valfritt)
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
            placeholder="Hur var energin, sömnen, humöret…?"
            className="mt-1 w-full px-3 py-2 rounded-xl border-2 border-black bg-white text-sm resize-none"
            data-testid="fasting-end-notes" />
        </label>
        <button onClick={() => onSubmit({ notes, mood })}
          className="neo-btn w-full py-3 bg-[#B9FBC0] font-bold"
          data-testid="fasting-end-confirm">
          Avsluta fastan
        </button>
      </div>
    </div>
  );
}

function FastingRing({ elapsed, target, milestones }) {
  // SVG dimensions
  const size = 320;
  const stroke = 22;
  const r = (size - stroke) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const circ = 2 * Math.PI * r;
  const pct = Math.min(elapsed / target, 1);
  const dashOffset = circ * (1 - pct);

  // Place milestones on circle (start at top, rotating clockwise)
  const milestonePoints = milestones
    .filter((m) => m.hours <= target)
    .map((m) => {
      const ratio = m.hours / target;
      const angle = -Math.PI / 2 + 2 * Math.PI * ratio;
      return {
        ...m,
        x: cx + r * Math.cos(angle),
        y: cy + r * Math.sin(angle),
        reached: elapsed >= m.hours,
      };
    });

  return (
    <div className="relative mx-auto" style={{ width: size, height: size }} data-testid="fasting-ring">
      <svg width={size} height={size} className="overflow-visible">
        <defs>
          <linearGradient id="fastGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#FFB3C7" />
            <stop offset="50%" stopColor="#FBBF24" />
            <stop offset="100%" stopColor="#34D399" />
          </linearGradient>
        </defs>
        {/* Background ring */}
        <circle cx={cx} cy={cy} r={r}
          fill="none" stroke="rgba(0,0,0,0.08)" strokeWidth={stroke} />
        {/* Progress arc */}
        <circle cx={cx} cy={cy} r={r}
          fill="none" stroke="url(#fastGrad)" strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circ}
          strokeDashoffset={dashOffset}
          transform={`rotate(-90 ${cx} ${cy})`}
          style={{ transition: 'stroke-dashoffset 800ms ease' }} />
        {/* Milestones */}
        {milestonePoints.map((m, i) => (
          <g key={i}>
            <circle cx={m.x} cy={m.y} r={18}
              fill={m.reached ? '#fff' : '#f5f5f5'}
              stroke="#000" strokeWidth={2.5} />
            <text x={m.x} y={m.y + 5} textAnchor="middle" fontSize={18}>
              {m.emoji}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
}

export default function FastingClock() {
  const [session, setSession] = useState(null);
  const [presets, setPresets] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState(null);
  const [showPresets, setShowPresets] = useState(false);
  const [showEnd, setShowEnd] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [loading, setLoading] = useState(true);
  const tickRef = useRef(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [a, p, h, s] = await Promise.all([
        axios.get(`${API}/fasting/active`, { withCredentials: true }),
        axios.get(`${API}/fasting/presets`, { withCredentials: true }),
        axios.get(`${API}/fasting/history?days=30`, { withCredentials: true }),
        axios.get(`${API}/fasting/stats`, { withCredentials: true }),
      ]);
      setSession(a.data.session);
      setPresets(p.data.presets || []);
      setMilestones(p.data.milestones || []);
      setHistory(h.data.sessions || []);
      setStats(s.data);
    } catch (e) {
      toast.error('Kunde inte ladda fasta');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Tick every second while active
  useEffect(() => {
    if (session) {
      tickRef.current = setInterval(() => setNow(Date.now()), 1000);
      return () => clearInterval(tickRef.current);
    }
  }, [session]);

  const elapsed = useMemo(() => {
    if (!session) return 0;
    const startMs = new Date(session.started_at).getTime();
    return Math.max(0, (now - startMs) / 1000);
  }, [session, now]);

  const elapsedHours = elapsed / 3600;
  const targetHours = session?.target_hours || 16;
  const remainingSec = Math.max(0, targetHours * 3600 - elapsed);
  const isComplete = elapsedHours >= targetHours;
  const endTimeIso = session ? new Date(new Date(session.started_at).getTime() + targetHours * 3600 * 1000).toISOString() : null;

  const startFast = async (preset_id, custom_hours) => {
    try {
      const body = { preset_id };
      if (preset_id === 'custom') body.target_hours = custom_hours;
      const { data } = await axios.post(`${API}/fasting/start`, body, { withCredentials: true });
      setSession(data.session);
      setShowPresets(false);
      toast.success(`${preset_id === 'custom' ? `${custom_hours}h` : preset_id} fasta startad 🔥`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunde inte starta');
    }
  };

  const endFast = async ({ notes, mood }) => {
    try {
      const { data } = await axios.post(`${API}/fasting/end`, { notes, mood }, { withCredentials: true });
      setShowEnd(false);
      setSession(null);
      toast.success(`+${data.points_earned} poäng · ${data.completed ? 'Klart! 🏆' : 'Tack för att du lyssnade 💗'}`);
      await fetchAll();
    } catch (e) {
      toast.error('Kunde inte avsluta');
    }
  };

  const cancelFast = async () => {
    if (!window.confirm('Avbryt fastan utan att räkna den?')) return;
    try {
      await axios.post(`${API}/fasting/cancel`, {}, { withCredentials: true });
      setSession(null);
      toast.info('Avbruten');
    } catch (e) {
      toast.error('Kunde inte avbryta');
    }
  };

  // Find next milestone
  const nextMilestone = session ? milestones.find((m) => m.hours > elapsedHours && m.hours <= targetHours) : null;
  const lastReached = session ? [...milestones].reverse().find((m) => m.hours <= elapsedHours) : null;

  if (loading) return <div className="neo-card p-8 text-center animate-pulse-soft">Laddar fastan…</div>;

  return (
    <div className="space-y-5" data-testid="fasting-clock">
      {/* Stats row */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <div className="neo-card p-3 bg-[#FFE4E9] text-center" data-testid="fasting-stat-streak">
            <Flame className="w-5 h-5 mx-auto text-rose-600" />
            <p className="text-xl font-bold font-['Fredoka']">{stats.streak}</p>
            <p className="text-[10px] opacity-70">Streak</p>
          </div>
          <div className="neo-card p-3 bg-[#FFF3B0] text-center">
            <Trophy className="w-5 h-5 mx-auto text-amber-600" />
            <p className="text-xl font-bold font-['Fredoka']">{stats.completed_fasts}</p>
            <p className="text-[10px] opacity-70">Klara fastor</p>
          </div>
          <div className="neo-card p-3 bg-[#BBF7D0] text-center">
            <p className="text-xl font-bold font-['Fredoka']">{stats.longest_hours}h</p>
            <p className="text-[10px] opacity-70">Längsta</p>
          </div>
          <div className="neo-card p-3 bg-[#A2D2FF] text-center">
            <p className="text-xl font-bold font-['Fredoka']">{stats.avg_hours}h</p>
            <p className="text-[10px] opacity-70">Snitt</p>
          </div>
        </div>
      )}

      <div className="neo-card p-6 bg-gradient-to-br from-[#FFF5F7] to-[#F5EBFF] dark:from-[#2a1a2e] dark:to-[#1f1428]">
        {!session ? (
          <div className="text-center py-8">
            <div className="text-6xl mb-3">🔥</div>
            <h2 className="text-3xl font-bold font-['Fredoka']">Redo att fasta?</h2>
            <p className="text-sm opacity-75 max-w-md mx-auto mt-2 mb-5">
              Välj ett upplägg så börjar klockan ticka. Du ser milstolpar längs vägen — när din kropp byter läge.
            </p>
            <button onClick={() => setShowPresets(true)}
              className="neo-btn px-6 py-3 bg-gradient-to-r from-rose-400 to-amber-400 text-white font-bold text-base inline-flex items-center gap-2"
              data-testid="fasting-start-btn">
              <Play className="w-5 h-5" /> Starta fasta
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-widest opacity-60">
                  {session.preset_label}-fasta
                </p>
                <h2 className="text-2xl font-bold font-['Fredoka']">
                  {isComplete ? 'Du klarade målet 🏆' : 'Fastan pågår'}
                </h2>
              </div>
              <button onClick={cancelFast}
                className="neo-btn px-3 py-1.5 bg-white text-xs font-bold flex items-center gap-1"
                data-testid="fasting-cancel-btn">
                <X className="w-3.5 h-3.5" /> Avbryt
              </button>
            </div>

            <div className="relative">
              <FastingRing elapsed={elapsedHours} target={targetHours} milestones={milestones} />
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <p className="text-[11px] font-bold uppercase tracking-widest opacity-60">
                  {isComplete ? 'Klart' : 'Kvar'}
                </p>
                <p className="text-5xl font-bold font-['Fredoka'] tabular-nums" data-testid="fasting-countdown">
                  {isComplete ? fmtHMS(elapsed) : fmtHMS(remainingSec)}
                </p>
                <p className="text-xs opacity-70 mt-1 tabular-nums">
                  {elapsedHours.toFixed(1)}h / {targetHours}h ({Math.round((elapsedHours / targetHours) * 100)}%)
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mt-5">
              <div className="neo-card p-3 bg-white dark:bg-[#252545] text-center" data-testid="fasting-start-card">
                <p className="text-[10px] uppercase tracking-widest opacity-60">Start</p>
                <p className="font-bold text-sm">{fmtClock(session.started_at)}</p>
              </div>
              <div className="neo-card p-3 bg-white dark:bg-[#252545] text-center" data-testid="fasting-end-card">
                <p className="text-[10px] uppercase tracking-widest opacity-60">Mål</p>
                <p className="font-bold text-sm">{fmtClock(endTimeIso)}</p>
              </div>
            </div>

            {nextMilestone && !isComplete && (
              <div className="mt-4 p-3 rounded-2xl border-2 border-black bg-[#FFF3B0]" data-testid="fasting-next-milestone">
                <p className="text-[10px] uppercase tracking-widest opacity-60 mb-1">Nästa milstolpe</p>
                <p className="text-sm">
                  <span className="text-xl mr-1">{nextMilestone.emoji}</span>
                  <b>{nextMilestone.label}</b> — om {(nextMilestone.hours - elapsedHours).toFixed(1)}h
                </p>
                <p className="text-[11px] opacity-70 mt-1">{nextMilestone.desc}</p>
              </div>
            )}
            {lastReached && (
              <div className="mt-3 p-3 rounded-2xl border-2 border-green-500 bg-[#BBF7D0]" data-testid="fasting-reached-milestone">
                <p className="text-[10px] uppercase tracking-widest opacity-60 mb-1">Du har nått</p>
                <p className="text-sm">
                  <span className="text-xl mr-1">{lastReached.emoji}</span>
                  <b>{lastReached.label}</b>
                </p>
                <p className="text-[11px] opacity-70 mt-1">{lastReached.desc}</p>
              </div>
            )}

            <button onClick={() => setShowEnd(true)}
              className="neo-btn w-full mt-5 py-3 bg-gradient-to-r from-green-400 to-emerald-500 text-white font-bold text-base flex items-center justify-center gap-2"
              data-testid="fasting-end-btn">
              <Square className="w-5 h-5" fill="currentColor" /> Avsluta fastan
            </button>
          </>
        )}
      </div>

      {/* Milestones timeline */}
      <div className="neo-card p-4 bg-white dark:bg-[#252545]" data-testid="fasting-milestones-timeline">
        <p className="text-[11px] uppercase tracking-widest opacity-60 font-bold mb-3">Milstolpar</p>
        <div className="space-y-2">
          {milestones.map((m, i) => {
            const reached = session && elapsedHours >= m.hours;
            return (
              <div key={i} className={`flex items-start gap-3 p-2 rounded-xl border-2 ${
                reached ? 'border-green-500 bg-[#BBF7D0]' : 'border-black/10 bg-[#FAFAFA] dark:bg-[#1f1f3a]'
              }`}>
                <div className="text-2xl">{m.emoji}</div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold font-['Fredoka']">{m.hours}h · {m.label}</p>
                  <p className="text-[11px] opacity-70">{m.desc}</p>
                </div>
                {reached && <Trophy className="w-4 h-4 text-green-700" />}
              </div>
            );
          })}
        </div>
      </div>

      {/* History */}
      {history.length > 0 && (
        <div className="neo-card p-4 bg-[#FFFAF0] dark:bg-[#1f1f3a]" data-testid="fasting-history">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[11px] uppercase tracking-widest opacity-60 font-bold">Senaste fastor</p>
            <button onClick={fetchAll} className="neo-btn p-1.5 bg-white" title="Uppdatera">
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {history.map((h) => (
              <div key={h.id} className="flex items-center justify-between gap-2 p-2 rounded-xl border-2 border-black bg-white dark:bg-[#252545] text-sm">
                <div>
                  <p className="font-bold">{h.preset_label || `${h.target_hours}h`}</p>
                  <p className="text-[10px] opacity-70">{new Date(h.started_at).toLocaleDateString('sv-SE', { day: '2-digit', month: 'short' })}</p>
                </div>
                <div className="text-right">
                  <p className="font-bold tabular-nums">{h.duration_hours?.toFixed(1) || '—'}h</p>
                  <p className="text-[10px] opacity-70">
                    {h.status === 'completed' ? '🏆 Klar' : '💗 Tidigt'}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {showPresets && (
        <PresetModal presets={presets} onPick={startFast} onClose={() => setShowPresets(false)} />
      )}
      {showEnd && (
        <EndModal elapsed={elapsedHours} target={targetHours}
          onSubmit={endFast} onClose={() => setShowEnd(false)} />
      )}
    </div>
  );
}
